import { useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import Container from "../components/Container.tsx";
import { useUserBookingsQuery, BookingResponseDto } from "../api/bookingApi.ts";
import {
  useUserSubscriptionsQuery,
  Subscription,
} from "../api/subscriptionApi.ts";
import {
  useScholarAppointmentsQuery,
  useTeacherAppointmentsQuery,
} from "../api/appointmentApi.ts";
import {
  useUserPendingChangeRequestsQuery,
  useUpdateChangeRequestMutation,
  BookingChangeRequestResponseDto,
} from "../api/bookingChangeRequestApi.ts";
import { t } from "@lingui/core/macro";
import { useAuth } from "react-oidc-context";
import { useUserDataQuery } from "../api/userApi.ts";
import {
  ChevronDown,
  GraduationCap,
  Music,
  Repeat,
  CalendarClock,
  CreditCard,
  List,
  CalendarDays,
} from "lucide-react";
import { Trans } from "@lingui/react/macro";
import { LoadingSpinner } from "../components/util/LoadingSpinner.tsx";
import AppointmentCard from "../components/dashboard/AppointmentCard.tsx";
import UserPendingSubscriptionCard from "../components/dashboard/UserPendingSubscriptionCard.tsx";
import UserPendingBookingCard from "../components/dashboard/UserPendingBookingCard.tsx";
import PendingChangeRequestCard from "../components/dashboard/PendingChangeRequestCard.tsx";
import DashboardEmptyState from "../components/dashboard/DashboardEmptyState.tsx";
import DashboardCalendarView from "../components/dashboard/DashboardCalendarView.tsx";
import { Tabs } from "../components/Tabs.tsx";
import { useNavigate } from "react-router";

// Lessons revealed per "Load more" click in the list view.
const LIST_PAGE_SIZE = 10;

const Dashboard: React.FC = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const { data: user, isLoading: isLoadingUser } = useUserDataQuery(
    auth.user?.profile.sub,
  );

  const [processingChangeRequestId, setProcessingChangeRequestId] =
    useState("");

  const isTeacher = !!user?.teacherStatus;

  const scholarQuery = useScholarAppointmentsQuery();
  const teacherQuery = useTeacherAppointmentsQuery();

  // How many lessons the list renders. Kept independent of the number of cached
  // pages so the list stays paginated even when the calendar tab has eagerly
  // loaded every page into the shared query cache.
  const [teacherVisible, setTeacherVisible] = useState(LIST_PAGE_SIZE);
  const [scholarVisible, setScholarVisible] = useState(LIST_PAGE_SIZE);

  const isLoadingScholar = scholarQuery.isLoading;
  const isLoadingTeacher = teacherQuery.isLoading;

  const scholarAppointments =
    scholarQuery.data?.pages.flatMap((p) => p.appointments) ?? [];
  const teacherAppointments =
    teacherQuery.data?.pages.flatMap((p) => p.appointments) ?? [];

  const scholarPages = scholarQuery.data?.pages;
  const teacherPages = teacherQuery.data?.pages;
  const scholarTotal =
    scholarPages != null && scholarPages.length > 0
      ? scholarPages[scholarPages.length - 1].total
      : 0;
  const teacherTotal =
    teacherPages != null && teacherPages.length > 0
      ? teacherPages[teacherPages.length - 1].total
      : 0;

  // Reveal another page in the list and fetch the next backend page if the
  // shared cache doesn't already hold enough loaded appointments.
  const loadMoreLessons = (
    query: typeof scholarQuery,
    setVisible: React.Dispatch<React.SetStateAction<number>>,
  ) => {
    setVisible((visible) => visible + LIST_PAGE_SIZE);
    if (query.hasNextPage && !query.isFetchingNextPage) {
      query.fetchNextPage().catch((e) => console.error(e));
    }
  };

  const { data: userSubscriptions } = useUserSubscriptionsQuery();
  const { data: userBookings } = useUserBookingsQuery();

  const { data: pendingChangeRequests, refetch: refetchChangeRequests } =
    useUserPendingChangeRequestsQuery();

  const { mutate: updateChangeRequest, isPending: isUpdatingChangeRequest } =
    useUpdateChangeRequestMutation();

  // ACCEPTED = teacher accepted, user needs to pay
  const pendingPaymentSubscriptions = useMemo<Subscription[]>(() => {
    return (userSubscriptions || []).filter((s) => s.status === "ACCEPTED");
  }, [userSubscriptions]);

  // INCOMPLETE = user submitted, waiting for teacher to accept — shown for teachers-as-students
  const pendingAcceptanceSubscriptions = useMemo<Subscription[]>(() => {
    return (userSubscriptions || []).filter((s) => s.status === "INCOMPLETE");
  }, [userSubscriptions]);

  // ACCEPTED bookings = teacher accepted, user needs to pay
  const pendingPaymentBookings = useMemo<BookingResponseDto[]>(() => {
    return (userBookings || []).filter((b) => b.status === "ACCEPTED");
  }, [userBookings]);

  const handleUpdateChangeRequest = (
    changeRequest: BookingChangeRequestResponseDto,
    status: "ACCEPTED" | "REJECTED",
    responseMessage?: string,
  ) => {
    setProcessingChangeRequestId(changeRequest.id);
    updateChangeRequest(
      {
        bookingId: changeRequest.bookingId,
        changeRequestId: changeRequest.id,
        dto: { status, responseMessage },
      },
      {
        onSuccess: () => {
          refetchChangeRequests().catch((e) => console.error(e));
        },
        onSettled: () => {
          setProcessingChangeRequestId("");
        },
      },
    );
  };

  const handleSuggestDifferentTime = (
    changeRequest: BookingChangeRequestResponseDto,
  ) => {
    navigate(
      `/user/${user?.id}/bookings/${changeRequest.bookingId}/move?counter=${changeRequest.id}`,
    );
  };

  if (isLoadingScholar || isLoadingTeacher || isLoadingUser) {
    return <LoadingSpinner loadingText={t`Loading dashboard...`} />;
  }

  const hasPendingPaymentSubscriptions = pendingPaymentSubscriptions.length > 0;
  const hasPendingAcceptanceSubscriptions =
    isTeacher && pendingAcceptanceSubscriptions.length > 0;
  const hasPendingPaymentBookings = pendingPaymentBookings.length > 0;
  const hasPendingChangeRequests = (pendingChangeRequests?.length ?? 0) > 0;
  const hasScholarLessons = scholarAppointments.length > 0;
  const hasTeacherLessons = isTeacher && teacherAppointments.length > 0;
  const hasAnyContent =
    hasPendingPaymentSubscriptions ||
    hasPendingAcceptanceSubscriptions ||
    hasPendingPaymentBookings ||
    hasPendingChangeRequests ||
    hasScholarLessons ||
    hasTeacherLessons;

  if (!hasAnyContent) {
    return (
      <Container>
        <DashboardEmptyState
          icon={Music}
          message={
            <Trans>
              No upcoming appointments. Find a teacher and book your first
              lesson!
            </Trans>
          }
          linkTo="/search"
          linkLabel={<Trans>Find a Teacher</Trans>}
        />
      </Container>
    );
  }

  return (
    <Container>
      <div className="flex flex-col gap-6 lg:flex-row lg:gap-8">
        {/* Pending items — sidebar on desktop */}
        {(hasPendingPaymentSubscriptions ||
          hasPendingAcceptanceSubscriptions ||
          hasPendingPaymentBookings ||
          hasPendingChangeRequests) && (
          <section className="flex flex-col gap-6 lg:order-last lg:w-80 lg:shrink-0">
            {/* Subscriptions awaiting teacher acceptance (teachers who also book lessons) */}
            {hasPendingAcceptanceSubscriptions && (
              <div>
                <div className="mb-3 flex items-center gap-2">
                  <Repeat className="h-5 w-5 text-amber-600" />
                  <h2 className="text-soluud-text-primary text-lg font-semibold">
                    <Trans>Pending Subscriptions</Trans>
                    <span className="ml-1.5 text-sm font-normal text-amber-700">
                      ({pendingAcceptanceSubscriptions.length})
                    </span>
                  </h2>
                </div>
                <div className="space-y-3">
                  {pendingAcceptanceSubscriptions.map((subscription) => (
                    <UserPendingSubscriptionCard
                      key={subscription.id}
                      subscription={subscription}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Subscriptions accepted by teacher, awaiting user payment */}
            {hasPendingPaymentSubscriptions && (
              <div>
                <div className="mb-3 flex items-center gap-2">
                  <Repeat className="h-5 w-5 text-blue-600" />
                  <h2 className="text-soluud-text-primary text-lg font-semibold">
                    <Trans>Subscriptions Awaiting Payment</Trans>
                    <span className="ml-1.5 text-sm font-normal text-blue-700">
                      ({pendingPaymentSubscriptions.length})
                    </span>
                  </h2>
                </div>
                <div className="space-y-3">
                  {pendingPaymentSubscriptions.map((subscription) => (
                    <UserPendingSubscriptionCard
                      key={subscription.id}
                      subscription={subscription}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Bookings accepted by teacher, awaiting user payment */}
            {hasPendingPaymentBookings && (
              <div>
                <div className="mb-3 flex items-center gap-2">
                  <CreditCard className="h-5 w-5 text-blue-600" />
                  <h2 className="text-soluud-text-primary text-lg font-semibold">
                    <Trans>Bookings Awaiting Payment</Trans>
                    <span className="ml-1.5 text-sm font-normal text-blue-700">
                      ({pendingPaymentBookings.length})
                    </span>
                  </h2>
                </div>
                <div className="space-y-3">
                  {pendingPaymentBookings.map((booking) => (
                    <UserPendingBookingCard
                      key={booking.id}
                      booking={booking}
                    />
                  ))}
                </div>
              </div>
            )}

            {hasPendingChangeRequests && (
              <div>
                <div className="mb-3 flex items-center gap-2">
                  <CalendarClock className="h-5 w-5 text-blue-600" />
                  <h2 className="text-soluud-text-primary text-lg font-semibold">
                    <Trans>Move Requests</Trans>
                    <span className="ml-1.5 text-sm font-normal text-blue-700">
                      ({pendingChangeRequests.length})
                    </span>
                  </h2>
                </div>
                <div className="space-y-3">
                  {pendingChangeRequests.map((request) => (
                    <PendingChangeRequestCard
                      key={request.id}
                      changeRequest={request}
                      userId={auth.user?.profile.sub ?? ""}
                      onAccept={() =>
                        handleUpdateChangeRequest(request, "ACCEPTED")
                      }
                      onReject={(reason) =>
                        handleUpdateChangeRequest(request, "REJECTED", reason)
                      }
                      onSuggest={() => handleSuggestDifferentTime(request)}
                      isProcessing={
                        isUpdatingChangeRequest &&
                        processingChangeRequestId === request.id
                      }
                    />
                  ))}
                </div>
              </div>
            )}
          </section>
        )}
        {/* Main content — upcoming lessons */}
        {(hasTeacherLessons || hasScholarLessons) && (
          <div className="min-w-0 flex-1">
            <Tabs paramKey="view" defaultValue="list">
              <Tabs.Tab
                value="list"
                label={
                  <span className="flex items-center gap-1.5">
                    <List className="h-4 w-4" />
                    <Trans>List</Trans>
                  </span>
                }
              >
                <div className="flex flex-col gap-8">
                  {/* As Teacher */}
                  {hasTeacherLessons && (
                    <section>
                      <div className="mb-3 flex items-center gap-2">
                        <GraduationCap className="text-soluud-secondary h-5 w-5" />
                        <h2 className="text-soluud-text-primary text-lg font-semibold">
                          <Trans>Lessons You're Teaching</Trans>
                          <span className="text-soluud-text-secondary ml-1.5 text-sm font-normal">
                            ({teacherTotal} - <Trans>Next 3 Months</Trans>)
                          </span>
                        </h2>
                      </div>

                      <div className="flex max-w-2xl flex-col gap-2">
                        {teacherAppointments
                          .slice(0, teacherVisible)
                          .map((appointment) => (
                            <AppointmentCard
                              key={`${appointment.subscriptionId ?? appointment.bookingId}-${appointment.from}`}
                              appointment={appointment}
                              showRelative={true}
                            />
                          ))}

                        {teacherVisible < teacherTotal && (
                          <button
                            onClick={() =>
                              loadMoreLessons(teacherQuery, setTeacherVisible)
                            }
                            disabled={teacherQuery.isFetchingNextPage}
                            className="text-soluud-primary hover:text-soluud-primary/80 mt-2 flex items-center gap-1.5 self-start text-sm font-medium transition-colors disabled:opacity-60"
                          >
                            {teacherQuery.isFetchingNextPage ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <ChevronDown className="h-4 w-4" />
                            )}
                            <Trans>Load more</Trans>
                          </button>
                        )}
                      </div>
                    </section>
                  )}
                  {/* As Scholar */}
                  {hasScholarLessons && (
                    <section>
                      <div className="mb-3 flex items-center gap-2">
                        <Music className="text-soluud-primary h-5 w-5" />
                        <h2 className="text-soluud-text-primary text-lg font-semibold">
                          <Trans>Lessons you are attending</Trans>
                          <span className="text-soluud-text-secondary ml-1.5 text-sm font-normal">
                            ({scholarTotal} - <Trans>Next 3 Months</Trans>)
                          </span>
                        </h2>
                      </div>

                      <div className="flex max-w-2xl flex-col gap-2">
                        {scholarAppointments
                          .slice(0, scholarVisible)
                          .map((appointment) => (
                            <AppointmentCard
                              key={`${appointment.subscriptionId ?? appointment.bookingId}-${appointment.from}`}
                              appointment={appointment}
                              showRelative={true}
                            />
                          ))}

                        {scholarVisible < scholarTotal && (
                          <button
                            onClick={() =>
                              loadMoreLessons(scholarQuery, setScholarVisible)
                            }
                            disabled={scholarQuery.isFetchingNextPage}
                            className="text-soluud-primary hover:text-soluud-primary/80 mt-2 flex items-center gap-1.5 self-start text-sm font-medium transition-colors disabled:opacity-60"
                          >
                            {scholarQuery.isFetchingNextPage ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <ChevronDown className="h-4 w-4" />
                            )}
                            <Trans>Load more</Trans>
                          </button>
                        )}
                      </div>
                    </section>
                  )}
                </div>
              </Tabs.Tab>
              <Tabs.Tab
                value="calendar"
                label={
                  <span className="flex items-center gap-1.5">
                    <CalendarDays className="h-4 w-4" />
                    <Trans>Calendar</Trans>
                  </span>
                }
              >
                <DashboardCalendarView
                  scholarQuery={scholarQuery}
                  teacherQuery={teacherQuery}
                  isTeacher={isTeacher}
                />
              </Tabs.Tab>
            </Tabs>
          </div>
        )}
      </div>
    </Container>
  );
};

export default Dashboard;
